import {
  Card,
  HorizontalScrollView,
  ReviewsSummary,
  Text,
  Title,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useGetRatingAggregates } from "@src/appV2/redesign/Workplace/Review/api/useGetRatingAggregates";
import { useGetWorkplaceSummaries } from "@src/appV2/redesign/Workplace/Review/api/useGetWorkplaceSummaries";
import { type PaginatedWorkplaceCommentsParams } from "@src/appV2/redesign/Workplace/Review/api/usePaginatedWorkplaceComments";
import { useWorkplaceComments } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { useHistory } from "react-router-dom";

import { Button } from "../../components/Button";
import {
  SHIFT_DISCOVERY_WORKPLACE_LEAVE_REVIEW_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_REVIEWS_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useWorkplaceModalsDataContext } from "../../ShiftDiscovery/useWorkplaceModalsDataContext";
import { WorkplaceHighlightPills } from "../Highlights/Pills";
import { useWorkplaceHighlights } from "../Highlights/useWorkplaceHighlights";
import { useHideReviewsUIByFacilityId } from "../hooks/useHideReviewsUIByFacilityId";
import { WorkplaceReviewCommentCompactCardContainer } from "../Review/Comment/CompactCardContainer";
import { useUpdateCachedComment } from "../Review/Comment/useUpdateCachedComment";
import { useCanLeaveReview } from "../Review/useCanLeaveReview";
import { WorkplaceReviewSummary } from "./WorkplaceReviewSummary";

export interface WorkplaceHighlightsAndReviewsCardProps {
  workplaceId: string;
  workplaceType: string | undefined;
}

export function WorkplaceHighlightsAndReviewsCard(props: WorkplaceHighlightsAndReviewsCardProps) {
  const { workplaceId, workplaceType } = props;

  const commentsParams: PaginatedWorkplaceCommentsParams = { workplaceId };

  const history = useHistory();

  const { generateModalPath } = useWorkplaceModalsDataContext();

  const { data: ratingAggregates, isSuccess: ratingAggregatesIsSuccess } =
    useGetRatingAggregates(workplaceId);

  const { highlights, isLoading: isHighlightsLoading } = useWorkplaceHighlights({ workplaceId });

  const { data: summaries, isSuccess: summariesIsSuccess } = useGetWorkplaceSummaries(workplaceId);

  const { facilityIds } = useHideReviewsUIByFacilityId();

  const showComments = !facilityIds.includes(workplaceId);

  const { data: comments, isSuccess: commentsIsSuccess } = useWorkplaceComments({ workplaceId });

  const { updateComment } = useUpdateCachedComment({
    workplaceId,
    commentsParams,
  });

  const { canLeaveReview } = useCanLeaveReview({ workplaceId, workplaceType });

  if (!ratingAggregatesIsSuccess || !commentsIsSuccess || !summariesIsSuccess) {
    return null;
  }

  const { avgRating, numberOfRatings } = ratingAggregates;

  const summary = summaries.data[0];

  return (
    <Card>
      <CardContent sx={{ padding: 7 }}>
        <Stack spacing={4}>
          <Title bold variant="h5" component="h5">
            Reviews
          </Title>
          <ReviewsSummary rating={avgRating ?? 0} ratingsCount={numberOfRatings ?? 0} />
        </Stack>

        {isDefined(summary) && (
          <WorkplaceReviewSummary
            summaryId={summary.id}
            summary={summary.attributes.summary}
            workplaceId={workplaceId}
            qualificationName={summary.attributes.qualificationName}
          />
        )}

        {highlights.length > 0 && (
          <>
            <Text semibold variant="body1" sx={{ marginTop: 8, marginBottom: 7 }}>
              Highlights
            </Text>

            <WorkplaceHighlightPills highlights={highlights} isLoading={isHighlightsLoading} />
          </>
        )}

        {showComments && (
          <>
            <HorizontalScrollView stretch sx={{ marginTop: 8 }} outerPaddingCompensation={7}>
              {comments.map((comment) => (
                <WorkplaceReviewCommentCompactCardContainer
                  key={comment.id}
                  comment={comment}
                  workplaceId={workplaceId}
                  onLikeChanged={(comment) => {
                    updateComment(comment);
                  }}
                  onClick={() => {
                    history.push(
                      generateModalPath(SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH, {
                        workplaceId,
                        commentId: comment.id,
                      })
                    );
                  }}
                />
              ))}
            </HorizontalScrollView>

            {comments.length > 0 ? (
              <Button
                fullWidth
                variant="outlined"
                size="medium"
                sx={{ marginTop: 8 }}
                href={generateModalPath(SHIFT_DISCOVERY_WORKPLACE_REVIEWS_MODAL_PATH, {
                  workplaceId,
                })}
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_SCREEN_OPENED, {
                    workplaceId,
                    trigger: "Workplace Profile Modal",
                  });
                }}
              >
                {comments.length > 1 ? "View all comments" : "View 1 comment"}
              </Button>
            ) : (
              <Button
                fullWidth
                variant="outlined"
                size="medium"
                sx={{ marginTop: 8 }}
                href={generateModalPath(SHIFT_DISCOVERY_WORKPLACE_LEAVE_REVIEW_MODAL_PATH, {
                  workplaceId,
                })}
                disabled={!canLeaveReview}
              >
                {canLeaveReview ? "Leave a Review" : "No written reviews yet"}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
